import { Input, InputChangeEvent } from '@progress/kendo-react-inputs';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { Button, IconButton, Icon as IconComp, IconPositionEnum, Variant } from '@indicia/component-library';
import useBem from '@indicia/use-bem';

import { postAnswers } from '../../../../../features/survey/data/SurveyService';
import useSurveyContext from '../../../../../features/survey/store/UseSurveyContext';
import Icon from '../../../button/enums/Icon';
import Dialog from '../../../dialog/Dialog';

import './DashboardNavigation.scss';

type NavigationLink = {
  to: string;
  icon: string;
  text: string;
};

type NavigationSection = {
  header: string;
  links: NavigationLink[];
};

const DashboardNavigationLinks = () => {
  const { t } = useTranslation();
  const { bemClassName } = useBem('header-bar');
  const { activationCode, setEmailAddress, getBackupQuestionAnswers, sessionKey } = useSurveyContext();

  const [showMailDialog, setShowMailDialog] = useState<boolean>(false);
  const [mail, setMail] = useState<string>('');

  const toggleMailDialog = () => {
    setShowMailDialog(!showMailDialog);
  };

  const submitMail = () => {
    setEmailAddress(mail);
    const answers = getBackupQuestionAnswers();
    postAnswers(answers, activationCode, true, mail, sessionKey).then(() => {
      toggleMailDialog();
    });
  };

  const overviewSection = {
    header: t('dashboard.navigation.overview-header'),
    links: [
      {
        to: '/dashboard',
        icon: Icon.wegwijzer,
        text: t('dashboard.navigation.overview'),
      },
    ],
  };

  const insightSection = {
    header: t('dashboard.navigation.insight-header'),
    links: [
      {
        to: '/improvements',
        icon: Icon.doel,
        text: t('dashboard.navigation.improvements'),
      },
      {
        to: '/suggestions',
        icon: Icon.succes,
        text: t('dashboard.navigation.suggestions'),
      },
      {
        to: '/thema',
        icon: Icon.settings,
        text: t('dashboard.navigation.change-your-age'),
      },
    ],
  };

  const renderSection = ({ header, links }: NavigationSection) => (
    <div className={bemClassName('section-group')}>
      <span className={bemClassName('section-header')}>{header}</span>
      {links.map(({ to, icon, text }) => (
        <NavLink
          key={to}
          to={to}
          end
          className={({ isActive }) => (isActive ? 'active-link' : '')}
        >
          <IconComp name={icon} />
          {text}
        </NavLink>
      ))}
    </div>
  );

  return (
    <>
      <nav className={bemClassName('navigation-container')}>
        {renderSection(overviewSection)}
        {renderSection(insightSection)}
        <div className={bemClassName('mail-section')}>
          <IconButton
            iconPosition={IconPositionEnum.Left}
            onClick={toggleMailDialog}
            variant={Variant.Plain}
            customClasses={bemClassName('mail-button')}
            iconName={Icon.email}
          >
            {t('dashboard.navigation.send-results')}
          </IconButton>
        </div>
      </nav>

      {showMailDialog && (
        <Dialog
          onClose={toggleMailDialog}
          title={t('dashboard.mail-dialog.title')!}
          content={
            <>
              <Trans
                i18nKey="dashboard.mail-dialog.content"
                components={{ 1: <br /> }}
              />
              <div className="mail-dialog__input">
                <Input
                  type="text"
                  value={mail}
                  placeholder={t('dashboard.mail-dialog.input')!}
                  onChange={(e: InputChangeEvent) => {
                    const {
                      target: { value },
                    } = e;
                    setMail(value as string);
                  }}
                />
              </div>
            </>
          }
          actions={
            <>
              <Button
                onClick={toggleMailDialog}
                variant={Variant.Plain}
              >
                {t('dashboard.mail-dialog.cancel')}
              </Button>
              <Button
                onClick={submitMail}
                variant={Variant.Plain}
              >
                {t('dashboard.mail-dialog.submit')!}
              </Button>
            </>
          }
        />
      )}
    </>
  );
};

export default DashboardNavigationLinks;
